import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { mlContentAnalysis } from '@/services/MLContentAnalysisService';
import { Brain, TrendingUp, Award, BarChart3 } from 'lucide-react';

export const ContentQualityDashboard = () => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const data = await mlContentAnalysis.getContentAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Brain className="h-6 w-6 animate-pulse text-primary" />
            <span className="ml-2">Analyzing 2,292+ enhanced modules...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  const enhancementRate = Math.round((analytics.enhancedModules / analytics.totalModules) * 100);
  const comprehensiveRate = Math.round((analytics.comprehensiveModules / analytics.totalModules) * 100);

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analytics.totalModules}</div>
          <p className="text-xs text-muted-foreground">
            Across all courses
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Enhanced Content</CardTitle>
          <TrendingUp className="h-4 w-4 text-success" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analytics.enhancedModules}</div>
          <Progress value={enhancementRate} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            {enhancementRate}% of all modules
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Comprehensive</CardTitle>
          <Award className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analytics.comprehensiveModules}</div>
          <Progress value={comprehensiveRate} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            {comprehensiveRate}% (≥2000 words)
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">ML Quality Score</CardTitle>
          <Brain className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analytics.averageQuality}</div>
          <Progress value={analytics.averageQuality * 10} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            AI-powered analysis
          </p>
        </CardContent>
      </Card>

      <Card className="md:col-span-2 lg:col-span-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Top Quality Modules (ML-Ranked)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics.topModules.slice(0, 5).map((module: any, index: number) => (
              <div key={module.moduleId} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Badge variant={index === 0 ? "default" : "outline"}>
                    #{index + 1}
                  </Badge>
                  <span className="font-medium">{module.moduleId}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={module.score * 10} className="w-24" />
                  <span className="text-sm font-semibold">{module.score.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
